/**
 * A minimal DNS-over-HTTPS client (RFC 8484).
 *
 * This exists to replace `dohjs`, which pulls in `dns-packet` and a `Buffer`
 * polyfill -- roughly 40KB and a `global` shim in the bundler config -- to do
 * exactly what is below: encode one question and read the answers back.
 * Everything here works on Uint8Array/DataView, so nothing is polyfilled.
 */

export const RECORD_TYPE = { A: 1, AAAA: 28, CNAME: 5, TXT: 16 } as const
export type RecordTypeName = keyof typeof RECORD_TYPE

const RCODE = [
    'NOERROR', 'FORMERR', 'SERVFAIL', 'NXDOMAIN', 'NOTIMP', 'REFUSED',
] as const

export interface DohAnswer {
    name: string
    type: number
    ttl: number
    data: string
}

export interface DohResult {
    rcode: string
    answers: DohAnswer[]
}

//#region encoding
/** Length-prefixed labels, null terminated. `example.com` -> 7example3com0 */
function encodeName(name: string): number[] {
    const out: number[] = []
    for (const label of name.replace(/\.$/, '').split('.')) {
        if (label.length === 0) continue
        if (label.length > 63) throw new Error(`DNS label too long: ${label}`)
        out.push(label.length)
        for (const ch of label) out.push(ch.charCodeAt(0))
    }
    out.push(0)
    return out
}

export function encodeQuery(name: string, type: RecordTypeName = 'A'): Uint8Array<ArrayBuffer> {
    const question = encodeName(name)
    // explicitly ArrayBuffer-backed so the result satisfies BodyInit
    const buf = new Uint8Array(new ArrayBuffer(12 + question.length + 4))
    const view = new DataView(buf.buffer)

    // id 0: RFC 8484 recommends it, since HTTP already correlates the exchange
    view.setUint16(0, 0)
    // recursion desired
    view.setUint16(2, 0x0100)
    // one question, no answer/authority/additional records
    view.setUint16(4, 1)

    buf.set(question, 12)
    view.setUint16(12 + question.length, RECORD_TYPE[type])
    view.setUint16(12 + question.length + 2, 1) // class IN

    return buf
}
//#endregion

//#region decoding
/**
 * Reads a name, following compression pointers.
 * Returns the offset *after* the name as encountered, which for a pointer is
 * two bytes on from where it started rather than wherever it pointed.
 */
function readName(buf: Uint8Array, view: DataView, start: number): { name: string; next: number } {
    const labels: string[] = []
    let offset = start
    let next = -1
    let hops = 0

    while (offset < buf.length) {
        const len = buf[offset]
        if (len === 0) { offset += 1; break }

        // 0b11xxxxxx marks a pointer to an earlier name
        if ((len & 0xc0) === 0xc0) {
            if (next === -1) next = offset + 2
            offset = view.getUint16(offset) & 0x3fff
            // a pointer loop would otherwise hang the parser
            if (++hops > 64) throw new Error('DNS name compression loop')
            continue
        }

        offset += 1
        labels.push(String.fromCharCode(...buf.subarray(offset, offset + len)))
        offset += len
    }

    return { name: labels.join('.'), next: next === -1 ? offset : next }
}

function readRdata(buf: Uint8Array, view: DataView, offset: number, type: number, length: number): string {
    if (type === RECORD_TYPE.A && length === 4) {
        return Array.from(buf.subarray(offset, offset + 4)).join('.')
    }
    if (type === RECORD_TYPE.AAAA && length === 16) {
        const parts: string[] = []
        for (let i = 0; i < 16; i += 2) parts.push(view.getUint16(offset + i).toString(16))
        return parts.join(':').replace(/(^|:)0(:0)+(:|$)/, '::')
    }
    if (type === RECORD_TYPE.CNAME) {
        return readName(buf, view, offset).name
    }
    if (type === RECORD_TYPE.TXT) {
        const len = buf[offset]
        return String.fromCharCode(...buf.subarray(offset + 1, offset + 1 + len))
    }
    return Array.from(buf.subarray(offset, offset + length))
        .map((b) => b.toString(16).padStart(2, '0')).join('')
}

export function decodeResponse(bytes: Uint8Array): DohResult {
    if (bytes.length < 12) throw new Error('DNS response too short')
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)

    const rcodeBits = view.getUint16(2) & 0x000f
    const rcode = RCODE[rcodeBits] ?? `RCODE${rcodeBits}`
    const questions = view.getUint16(4)
    const answerCount = view.getUint16(6)

    let offset = 12
    for (let i = 0; i < questions; i++) {
        offset = readName(bytes, view, offset).next + 4 // + qtype, qclass
    }

    const answers: DohAnswer[] = []
    for (let i = 0; i < answerCount && offset < bytes.length; i++) {
        const { name, next } = readName(bytes, view, offset)
        offset = next
        const type = view.getUint16(offset)
        const ttl = view.getUint32(offset + 4)
        const rdLength = view.getUint16(offset + 8)
        offset += 10
        answers.push({ name, type, ttl, data: readRdata(bytes, view, offset, type, rdLength) })
        offset += rdLength
    }

    return { rcode, answers }
}
//#endregion

export interface DohQueryOptions {
    timeoutMs?: number
    signal?: AbortSignal
}

export interface DohMeasurement extends DohResult {
    /** Round trip as the browser sees it: includes TLS setup on a cold connection. */
    latencyMs: number
}

/**
 * Resolves `name` through `endpoint` and times it.
 *
 * Rejects rather than resolving on transport failure, so callers can tell
 * "the resolver said NXDOMAIN" (a result) from "we could not reach it at all"
 * (unreachable) -- a distinction that matters on networks where most of these
 * endpoints are blocked.
 */
export async function dohQuery(
    endpoint: string,
    name: string,
    type: RecordTypeName = 'A',
    { timeoutMs = 3000, signal }: DohQueryOptions = {},
): Promise<DohMeasurement> {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)
    signal?.addEventListener('abort', () => controller.abort(), { once: true })

    const started = performance.now()
    try {
        const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'content-type': 'application/dns-message', accept: 'application/dns-message' },
            body: encodeQuery(name, type),
            signal: controller.signal,
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const bytes = new Uint8Array(await res.arrayBuffer())
        const latencyMs = performance.now() - started
        return { ...decodeResponse(bytes), latencyMs }
    } finally {
        clearTimeout(timer)
    }
}
